import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    signal,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CriterioTipoDado } from '@shared/criterio';
import {
    DataManagerConfig,
    DataManagerController,
    DataManagerDefinicaoColunaCalculada,
    DataManagerDefinicaoColunaLookup,
    DataManagerDefinicaoColunaSimples,
    DataManagerPreferenciaUsuario,
    DataManagerProvider,
} from '@shared/infra/data-manager';
import { VendaCampos } from '@shared/venda/ui';
import { map, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { DashboardPreferenciaCardOrigemModel } from '../../models/dashboard-config.model';
import {
    DashboardCardModel,
    DashboardCardTipo,
    DashboardDadoCardItemModel,
} from '../../models/dashboard.model';
import { DashboardService } from '../../services/dashboard.service';
import { DashboardCardChartComponent } from './chart/dashboard-card-chart.component';
import { DashboardCardListComponent } from './list/dashboard-card-list.component';

@Component({
  selector: 'app-dashboard-card',
  templateUrl: './dashboard-card.component.html',
  styleUrl: './dashboard-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkPortalOutlet,
    DashboardCardListComponent,
    DashboardCardChartComponent,
  ],
})
export class DashboardCardComponent
  implements DataManagerProvider<DashboardDadoCardItemModel>
{
  cardId = input.required<string>();

  // Injectables
  private cdr = inject(ChangeDetectorRef);
  private dashboardSvc = inject(DashboardService);
  private destroyRef = inject(DestroyRef);
  private overlay = inject(Overlay);
  private viewRef = inject(ViewContainerRef);

  // Private
  private configSubject = new Subject<DashboardCardModel>();
  private contentTemplate = viewChild<TemplateRef<unknown>>('content');
  private overlayRef!: OverlayRef;
  private portalOutlet = viewChild(CdkPortalOutlet);
  private prefUsuarioCard = computed(() => {
    const prefUsuario = this.dashboardSvc.getPreferenciaUsuario();
    const prefUsuarioCard = prefUsuario().cards.find(
      (c) => c.id === this.cardId()
    );
    return prefUsuarioCard || null;
  });
  private templatePortal!: TemplatePortal;

  // Protected
  protected card = signal<DashboardCardModel | null>(null);
  protected fullScreenHabilitado = false;
  protected dmController?: DataManagerController<DashboardDadoCardItemModel>;
  protected readonly tipoCard = DashboardCardTipo;
  protected origemDados?: DashboardPreferenciaCardOrigemModel;

  // Effect
  protected onOrigemDadosChanged(
    origemDados: DashboardPreferenciaCardOrigemModel | undefined
  ) {
    this.origemDados = origemDados;
    this.dashboardSvc.updatePreferenciaUsuarioCard(
      this.cardId(),
      undefined,
      this.origemDados
    );
    this.dmController?.refresh();
  }

  private prefUsuarioCardChanged = effect(() => {
    const prefUsuarioCard = this.prefUsuarioCard();
    this.origemDados = prefUsuarioCard?.origem;
  });

  // Lifecycle
  ngOnInit(): void {
    this.initDmController();
  }

  ngAfterViewInit(): void {
    this.templatePortal = new TemplatePortal(
      this.contentTemplate()!,
      this.viewRef
    );
    this.portalOutlet()?.attach(this.templatePortal);

    const overlayConfig = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      hasBackdrop: true,
      width: '98vw',
      height: '98vh',
      panelClass: 'slc-overlay-fullscreen',
    });

    this.overlayRef = this.overlay.create(overlayConfig);
    this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event) => {
          if (event.key === 'Escape') {
            this.alternarFullscreen(false);
          }
        },
      });

    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // Public
  getConfig(): Observable<DataManagerConfig> {
    return this.configSubject.pipe(
      take(1),
      map((card) => {
        const dimensoes = card.dimensoes.map((dim) => ({
          id: dim.id,
          dadosAdicionais: dim.dadosAdicionais,
        }));
        const valores = card.valores.map((val) => ({
          id: val,
          dadosAdicionais: [],
        }));
        return { colunas: dimensoes.concat(valores), filtros: [] };
      })
    );
  }

  getPreferencias(): Observable<DataManagerPreferenciaUsuario | null> {
    return of(this.prefUsuarioCard()?.prefUsuario || null);
  }

  getDados(
    filtro: string | null,
    limiteLinhas?: number
  ): Observable<DashboardDadoCardItemModel[]> {
    const cardCache = this.card();
    if (cardCache && cardCache?.dados) {
      return of(this.consolidaItens());
    }

    return this.dashboardSvc.getCard(this.cardId()).pipe(
      tap((card) => {
        this.card.set(card);
        this.configSubject.next(card);
      }),
      switchMap((card) => {
        // Os dados normalmente virão na primeira chamada
        if (card.dados) {
          return of(card.dados);
        }
        // Porém se o card for novo e estiver sendo calculado ainda,
        // será necessário aguardar os dados ficarem prontos
        return this.dashboardSvc.getDadosAsync(card.id);
      }),
      map((dados) => {
        const card = this.card()!;
        card.dados = dados;
        return this.consolidaItens();
      })
    );
  }

  getDefinicaoColunas(): {
    colunasSimples?: DataManagerDefinicaoColunaSimples[];
    colunasCalculadas?: DataManagerDefinicaoColunaCalculada[];
    colunasLookup?: DataManagerDefinicaoColunaLookup[];
  } {
    const colunasSimples = Object.values(VendaCampos);
    return { colunasSimples };
  }

  resetPreferencias(): Observable<void> {
    throw new Error('Method not implemented.');
  }

  protected alternarFullscreen(alternarPara?: boolean) {
    this.fullScreenHabilitado =
      alternarPara === undefined ? !this.fullScreenHabilitado : alternarPara;
    if (this.fullScreenHabilitado) {
      this.portalOutlet()?.detach();
      this.overlayRef.attach(this.templatePortal);
    } else {
      this.portalOutlet()?.attach(this.templatePortal);
      this.overlayRef.detach();
    }
  }

  private consolidaItens() {
    const origemVenda = this.origemDados?.venda;
    const origemHistorico = this.origemDados?.historico;
    const vendas = this.card()?.dados?.itens;
    const historico = this.card()?.dados?.itensHistorico;

    const dimensoes = this.card()!.dimensoes;
    let itens: DashboardDadoCardItemModel[];

    if ((!origemVenda && !origemHistorico) || (!vendas && !historico)) {
      return [];
    } else if (origemVenda && !origemHistorico) {
      itens = vendas!.map((v) => ({ ...v }));
    } else if (!origemVenda && origemHistorico) {
      itens = historico!.map((v) => ({ ...v }));
    } else {
      const valores = this.card()!.valores;
      itens = vendas!.map((v) => ({ ...v }));
      for (const histItem of historico!) {
        const item = itens.find(
          (i) => !dimensoes.some((dim) => i[dim.id] !== histItem[dim.id])
        );
        if (item) {
          valores.forEach((val) => (item[val] += histItem[val]));
        } else {
          itens.push({ ...histItem });
        }
      }
    }

    const itensSorted = itens.sort((a, b) => {
      for (const dim of dimensoes) {
        const sortItem =
          dim.tipoDado === CriterioTipoDado.alfanumerico
            ? a[dim.id]?.localeCompare(b[dim.id])
            : dim.tipoDado === CriterioTipoDado.data
            ? parseInt(a[dim.id]) - parseInt(b[dim.id])
            : a[dim.id] - b[dim.id];
        if (sortItem !== 0) {
          return sortItem;
        }
      }
      return 0;
    });

    return itensSorted;
  }

  private initDmController() {
    const cardid = this.cardId();
    if (!cardid) {
      return;
    }

    const dmController = this.dashboardSvc.getDmController(cardid, this);
    dmController.init();
    // Como não temos filtros, temos que disparar o refresh já no início
    dmController.refresh();

    this.dmController = dmController;

    this.dmController.preferenciasChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pref) => {
          this.dashboardSvc.updatePreferenciaUsuarioCard(this.cardId(), pref);
        },
      });
  }
}
